/**
 * Shared number formatting utilities with Persian (fa-IR) locale support.
 * All functions return Persian digits (۰۱۲۳۴۵۶۷۸۹) with proper separators.
 */

/**
 * Format a value as Toman.
 * Input is in IRR (Rial), output is in Toman (IRR ÷ 10).
 *
 * Decimal places adapt to the magnitude of the Toman value:
 *   ≥ 10 Toman → 1 decimal place
 *   ≥ 1 Toman  → 2 decimal places
 *   ≥ 0.1 Toman → 3 decimal places
 *   < 0.1 Toman → up to 6 decimal places (to show tiny costs)
 *
 * Examples:
 *   0 IRR           → "۰"
 *   1 IRR           → "۰٫۱"
 *   5 IRR           → "۰٫۵"
 *   10 IRR          → "۱"
 *   999999 IRR      → "۹۹٬۹۹۹٫۹"
 *   1000000 IRR     → "۱۰۰٬۰۰۰"
 *   0.078 IRR       → "۰٫۰۰۷۸"
 *   0.0001 IRR      → "۰٫۰۰۰۰۱"
 */
export function formatToman(balance: string | number | undefined | null): string {
  if (balance === undefined || balance === null) return "۰";
  const num = typeof balance === "string" ? parseFloat(balance) : Number(balance);
  if (isNaN(num) || num === 0) return "۰";

  const toman = num / 10;
  const absToman = Math.abs(toman);

  // Pick decimal places to match the magnitude
  let maxDecimals: number;
  if (absToman >= 10) {
    maxDecimals = 1;
  } else if (absToman >= 1) {
    maxDecimals = 2;
  } else if (absToman >= 0.1) {
    maxDecimals = 3;
  } else {
    maxDecimals = 6;
  }

  return toman.toLocaleString("fa-IR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  });
}

/**
 * Format a value as whole Toman (IRR ÷ 10), always without decimals.
 * Used where prices must be shown as round Tomans.
 */
export function formatTomanInt(balance: string | number | undefined | null): string {
  if (balance === undefined || balance === null) return "۰";
  const num = typeof balance === "string" ? parseFloat(balance) : Number(balance);
  if (isNaN(num) || num === 0) return "۰";
  return Math.round(num / 10).toLocaleString("fa-IR");
}

/**
 * Parse a string that may contain Persian/Arabic-Indic digits into a number.
 * Strips non-numeric characters (commas, spaces) and converts Persian digits.
 *
 * Examples:
 *   "۵۰۰۰۰"   → 50000
 *   "50,000"  → 50000
 *   "۱۰۰٬۰۰۰" → 100000
 *   "abc"     → NaN
 */
export function parsePersianNumber(value: string): number {
  const latin = value
    // Persian/Arabic-Indic digits
    .replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)))
    .replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)))
    // Remove thousands separators (commas, Arabic/Persian separators)
    .replace(/[,٬،\s]/g, "");
  return parseInt(latin, 10);
}

/**
 * Convert ASCII/Latin digits in a string to Persian digits (۰-۹).
 * Non-digit characters are preserved as-is.
 *
 * Examples:
 *   "50000"  → "۵۰۰۰۰"
 *   "123abc" → "۱۲۳abc"
 */
export function toPersianDigits(value: string): string {
  return value.replace(/[0-9]/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);
}

/**
 * Convert a numeric value that may be in scientific notation to a plain
 * decimal string without exponent. Avoids `1E+4` → `۱E+۴` issues when
 * the value is later passed through `toPersianDigits`.
 *
 * Handles both number and string inputs (e.g. Decimal from the API may be
 * serialized as `"1E+4"`). For integers the result is an integer string;
 * for decimals trailing zeros are trimmed.
 *
 * Examples:
 *   "1E+4"     → "10000"
 *   "1.5E+3"   → "1500"
 *   1e4        → "10000"
 *   "10000.00" → "10000"
 *   "1.20"     → "1.2"
 *   "1E-7"     → "0.0000001"
 */
export function decimalToPlainString(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return "";
  let s = String(value).trim();
  if (s === "" || s.toLowerCase() === "null" || s.toLowerCase() === "undefined") return "";
  // Strip leading plus sign
  if (s.startsWith("+")) s = s.slice(1);

  // Fast path: no exponent
  if (!/[eE]/.test(s)) {
    if (s.includes(".")) {
      // Trim trailing zeros: "10000.00" -> "10000", "1.200" -> "1.2"
      const trimmed = s.replace(/\.?0+$/, "");
      if (trimmed === "" || trimmed === "-" ) return "0";
      // Avoid "-0"
      if (trimmed === "-0") return "0";
      return trimmed;
    }
    return s;
  }

  // Expand scientific notation via string manipulation to avoid precision loss
  const match = s.match(/^([+-])?(\d+)(?:\.(\d*))?[eE]([+-]?\d+)$/);
  if (!match) {
    const n = Number(s);
    if (isNaN(n)) return s;
    // Fallback: use fixed notation then trim
    const fixed = n.toFixed(20).replace(/\.?0+$/, "");
    return fixed === "-0" ? "0" : fixed;
  }
  const sign = match[1] === "-" ? "-" : "";
  const intPart = match[2];
  const fracPart = match[3] || "";
  const exp = parseInt(match[4], 10);
  const digits = intPart + fracPart;
  const decimalPos = intPart.length + exp;

  let result: string;
  if (decimalPos <= 0) {
    result = "0." + "0".repeat(-decimalPos) + digits;
  } else if (decimalPos >= digits.length) {
    result = digits + "0".repeat(decimalPos - digits.length);
  } else {
    result = digits.slice(0, decimalPos) + "." + digits.slice(decimalPos);
  }

  if (result.includes(".")) {
    result = result.replace(/0+$/, "").replace(/\.$/, "");
  }
  // Strip leading zeros but keep at least one digit before dot
  result = result.replace(/^0+(?=\d)/, "");
  if (result === "" || result.startsWith(".")) result = "0" + result;
  if (result.startsWith("-.")) result = "-0." + result.slice(2);
  if (result === "-0") return "0";
  return sign + result;
}

/**
 * Normalize any digit style (Persian, Arabic-Indic, or Latin) to ASCII digits,
 * stripping everything that is not a digit.
 *
 * Examples:
 *   "۵۰۰۰۰" → "50000"
 *   "50,000" → "50000"
 */
export function normalizeDigits(value: string): string {
  return value
    .replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)))
    .replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)))
    .replace(/[^0-9]/g, "");
}

/**
 * Like normalizeDigits, but keeps the decimal point (`.`).
 * Use for float/decimal fields.
 */
export function normalizeDecimal(value: string): string {
  return value
    .replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)))
    .replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)))
    .replace(/[^0-9.]/g, "")
    .replace(/(\..*?)\./g, "$1"); // keep only the first dot
}

/**
 * Format a number for display.
 * - Values >= 1 are rounded to whole numbers with thousands separators.
 * - Values < 1 show up to 4 decimal places (no grouping).
 *
 * Examples:
 *   0        → "۰"
 *   0.078    → "۰٫۰۷۸"
 *   0.0000   → "۰"
 *   1        → "۱"
 *   1000     → "۱٬۰۰۰"
 *   99999.9  → "۱۰۰٬۰۰۰"
 */
export function formatNumber(value: number | string | undefined | null): string {
  if (value === undefined || value === null) return "۰";
  const num = typeof value === "string" ? parseFloat(value) : Number(value);
  if (isNaN(num)) return "۰";

  if (num === 0) return "۰";

  if (num < 1) {
    return num.toLocaleString("fa-IR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 4,
      useGrouping: false,
    });
  }

  return Math.round(num).toLocaleString("fa-IR");
}

/**
 * Format a USD value for display with Persian digits.
 * Decimal places adapt to the magnitude so tiny costs remain readable.
 *
 * Examples:
 *   0      → "۰"
 *   0.0005 → "۰٫۰۰۰۵"
 *   0.87   → "۰٫۸۷"
 *   123    → "۱۲۳"
 */
export function formatDollar(value: number | string | undefined | null): string {
  const v = Number(value) || 0;
  if (v >= 100) return v.toLocaleString("fa-IR", { maximumFractionDigits: 0 });
  if (v >= 1) return v.toLocaleString("fa-IR", { maximumFractionDigits: 2 });
  if (v >= 0.001) return v.toLocaleString("fa-IR", { maximumFractionDigits: 4 });
  return v.toLocaleString("fa-IR", { maximumFractionDigits: 6 });
}

/**
 * Format a date as a relative Persian time string (e.g., "۲ ساعت پیش", "دیروز").
 * Falls back to the full Jalali date for older timestamps.
 */
export function relativeTime(dateStr: string | undefined | null): string {
  if (!dateStr) return "—";
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return "همین الان";
  if (diffMin < 60) return `${diffMin.toLocaleString("fa-IR")} دقیقه پیش`;
  if (diffHour < 24) return `${diffHour.toLocaleString("fa-IR")} ساعت پیش`;
  if (diffDay < 2) return "دیروز";
  if (diffDay < 7) return `${diffDay.toLocaleString("fa-IR")} روز پیش`;
  return new Date(dateStr).toLocaleDateString("fa-IR");
}

/**
 * Format a compact value with a magnitude suffix (K/M/B) and Persian digits.
 * Used for large counts like tokens.
 *
 * Examples:
 *   519          → "۵۱۹"
 *   19400        → "۱۹٫۴K"
 *   113000000    → "۱۱۳M"
 *   519000000    → "۵۱۹M"
 */
export function formatCompact(value: number | string | undefined | null): string {
  const v = Number(value) || 0;
  const fmt = (n: number) => n.toLocaleString("fa-IR", { maximumFractionDigits: 1 });
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return `${fmt(v / 1_000_000_000)}B`;
  if (abs >= 1_000_000) return `${fmt(v / 1_000_000)}M`;
  if (abs >= 1_000) return `${fmt(v / 1_000)}K`;
  return fmt(v);
}
